import React from 'react';
import { DashboardWeeklySupplierPerformance } from '../../../api/dashboardApi';
import {
    formatNumber,
    SLOT_COLORS,
    DEFAULT_SLOT_NAMES,
} from '@/features/dashboard/utils/dashboardUtils';

type SortColumn = 'finishCnt' | 'acceptanceRate' | 'rejectCnt';

interface RealtimePerformanceTableProps {
    suppliers: DashboardWeeklySupplierPerformance[];
    currentDate: string;
    sortColumn: SortColumn | null;
    sortDir: 'asc' | 'desc';
    onCycleSort: (col: SortColumn) => void;
}

const RealtimePerformanceTable: React.FC<RealtimePerformanceTableProps> = ({
                                                                               suppliers,
                                                                               currentDate,
                                                                               sortColumn,
                                                                               sortDir,
                                                                               onCycleSort,
                                                                           }) => {
    const sortIcon = (col: SortColumn) => {
        if (sortColumn !== col) return 'fa-sort';
        return sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    };

    if (suppliers.length === 0) {
        return (
            <table className="table dashboard-weekly-table supplier-realtime-table">
                <tbody>
                <tr>
                    <td colSpan={6} className="empty-message">
                        협력사별 수행 현황 데이터가 없습니다.
                    </td>
                </tr>
                </tbody>
            </table>
        );
    }

    return (
        <table className="table dashboard-weekly-table supplier-realtime-table">
            <thead>
            <tr>
                <th>협력사명</th>
                <th>세트</th>
                <th
                    onClick={() => onCycleSort('finishCnt')}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                    총 완료 건수{' '}
                    <i
                        className={`fas fa-fw ${sortIcon('finishCnt')}`}
                        style={{ opacity: sortColumn === 'finishCnt' ? 1 : 0.4 }}
                    />
                </th>
                <th
                    onClick={() => onCycleSort('acceptanceRate')}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                    수락률{' '}
                    <i
                        className={`fas fa-fw ${sortIcon('acceptanceRate')}`}
                        style={{ opacity: sortColumn === 'acceptanceRate' ? 1 : 0.4 }}
                    />
                </th>
                <th
                    onClick={() => onCycleSort('rejectCnt')}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                    거절/취소{' '}
                    <i
                        className={`fas fa-fw ${sortIcon('rejectCnt')}`}
                        style={{ opacity: sortColumn === 'rejectCnt' ? 1 : 0.4 }}
                    />
                </th>
                <th className="col-slot-achievement">시간대별 달성</th>
            </tr>
            </thead>
            <tbody>
            {suppliers.map((supplier, index) => {
                const slots = supplier.dailySlots?.[currentDate] || [];
                const slotCounts = supplier.dailySlotCounts?.[currentDate] || [];
                const slotFinishCnt = slotCounts.reduce(
                    (sum: number, countStr: string) =>
                        sum + (parseInt(countStr.split('/')[0]) || 0),
                    0,
                );
                const slotNames =
                    supplier.dailySlotNames?.[currentDate] ??
                    (supplier.slotNames && supplier.slotNames.length > 0
                        ? supplier.slotNames
                        : DEFAULT_SLOT_NAMES);
                const rejectCnt =
                    (supplier.rejectCnt ?? 0) +
                    (supplier.assignCancelCnt ?? 0) +
                    (supplier.deliCancelCnt ?? 0);

                return (
                    <tr
                        key={supplier.suppId}
                        className={index % 2 === 0 ? 'row-even' : 'row-odd'}
                    >
                        <td>{supplier.suppName}</td>
                        <td>{supplier.slotSetCnt ?? '-'}</td>
                        <td>{formatNumber(slotFinishCnt)}</td>
                        <td>{(supplier.acceptanceRate ?? 0).toFixed(1)}%</td>
                        <td>{formatNumber(rejectCnt)}</td>
                        <td className="col-slot-achievement-cell">
                            <div className="slot-achievement-inline">
                                {slotNames.map((name, i) => {
                                    const color = SLOT_COLORS[i % SLOT_COLORS.length];
                                    const isNa =
                                        slotCounts[i] === '-' || slots[i] === '-';
                                    return (
                                        <div key={i} className="slot-achievement-item">
                                                <span
                                                    className="slot-achievement-label"
                                                    style={{ color: color.headerText }}
                                                >
                                                    {name}
                                                </span>
                                            {(supplier.dailySlotTimeRanges?.[currentDate]?.[i] ??
                                                supplier.slotTimeRanges?.[i]) && (
                                                <span className="slot-achievement-time">
                                                        {supplier.dailySlotTimeRanges?.[
                                                            currentDate
                                                            ]?.[i] ?? supplier.slotTimeRanges![i]}
                                                    </span>
                                            )}
                                            {isNa ? (
                                                <span className="slot-na">—</span>
                                            ) : (
                                                <>
                                                        <span className="slot-achievement-count">
                                                            {slotCounts[i] || '0/0'}
                                                        </span>
                                                    <span
                                                        className="slot-achievement-rate"
                                                        style={{ color: color.rateText }}
                                                    >
                                                            {slots[i] || '0%'}
                                                        </span>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </td>
                    </tr>
                );
            })}
            </tbody>
        </table>
    );
};

export default RealtimePerformanceTable;
